import { Injectable } from '@angular/core';
import { BasketItem, CombinedBasketItem } from '../basket';
import { BookingTypeName } from '@shared/models/enums';
import { BookingPeriodExtended, Resource } from '@features/booking/calender/model/models';
import { BookingPeriod } from '@shared/models/bookingPeriod';
import { Activity } from '@app/shared/models/activity';
import { SearchResponseTimeslot } from '../../../models/search-response';

/**
 * Context for converting basket items to booking periods
 */
interface BasketConversionContext {
  activity: Activity;
  resource: Resource;
  bookingId: number;
}

/** What the basket's Gentag view (#1138) needs to repeat an item. */
export interface BasketRepeat {
  item: CombinedBasketItem;
  selectedPeriod: BookingPeriod;
  /** The basket as booking periods: a repeat may not overlap them. */
  currentPeriods: BookingPeriod[];
}

/**
 * The basket's "Gentag periode": turns a basket item and the basket into what the shared
 * RepeatedBookingComponent takes, and its chosen periods back into timeslots for the basket.
 */
@Injectable({
  providedIn: 'root',
})
export class BasketRepeatableService {
  repeatOf(
    basketItem: CombinedBasketItem,
    basketItems: CombinedBasketItem[],
    bookingId: number,
    activity: Activity,
    resource: Resource,
  ): BasketRepeat {
    const context = { activity, resource, bookingId };
    return {
      item: basketItem,
      selectedPeriod: this.convertBasketItemToBookingPeriod(basketItem, context),
      currentPeriods: basketItems.map((item) => this.convertBasketItemToBookingPeriod(item, context)),
    };
  }

  /** The chosen periods as timeslots, of the repeated item's availability type. */
  toTimeslots(periods: BookingPeriodExtended[], item: BasketItem): SearchResponseTimeslot[] {
    return periods.map((period): SearchResponseTimeslot => ({
      id: null, // set by the basket when adding, which knows the anlæg
      resourceId: period.resourceId,
      start: period.start,
      end: period.end,
      activityId: period.activityId,
      locationId: null, // set by the basket when adding
      price: period.price,
      isAvailable: true,
      availabilityType: item.availabilityType,
      bookingTypeId: period.bookingTypeId,
    }));
  }

  /**
   * Convert a basket item to BookingPeriod format for repeated booking component
   */
  private convertBasketItemToBookingPeriod(item: CombinedBasketItem, context: BasketConversionContext): BookingPeriod {
    return {
      id: null,
      bookingId: context.bookingId || null,
      resourceId: item.resourceId,
      activityId: item.activityId,
      start: item.start,
      end: item.end,
      price: item.price,
      bookingTypeId: item.bookingTypeId || BookingTypeName.UNION, // Use stored value or default to UNION
      activity: context.activity,
      resource: context.resource,
    } as BookingPeriod;
  }
}
